/**
 * @file src/ui/react/studio/useOfflineCollection.ts
 * @description The offline library, as React state.
 *
 * Everything real happens in `src/offline/`; this only re-reads it when it changes and hands the
 * answers to a component. Two decisions worth naming:
 *
 * **Nothing is cached in React.** Every refresh re-reads the store, which re-checks the bucket. A
 * downloaded collection whose bytes the browser reclaimed has to stop calling itself ready the next
 * time anyone looks, and a copy of the last-known answer sitting in a hook would be exactly the
 * thing that kept saying yes.
 *
 * **Progress arrives through the same subscription as everything else.** A download ticking is a
 * library change like any other, so there is one notification path and no polling.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  OFFLINE_CHANGE,
  OfflineCapError,
  cancelCollectionDownload,
  downloadCollection,
  readCollectionOfflineStatus,
  readDownloadProgress,
  readOfflineLibrary,
  refreshOfflineSnapshot,
  removeDownloadedCollection,
  resumeCollectionDownload,
  subscribeToOfflineLibrary,
} from '../../../offline/offlineLibrary.js';
import { isOfflineDownloadsEnabled } from '../../../offline/offlineConfig.js';

/** The two qualities a download may be kept at. Compressed is the MP3, lossless the normalized WAV. */
export type OfflineQuality = 'compressed' | 'lossless';

export interface OfflineCollectionStatus {
  collectionId: string;
  /** What was downloaded. `collection` today; the field exists so another entity can join later. */
  kind?: string;
  name: string | null;
  coverImage?: string | null;
  /** The cover's logical key in the bucket. The remote URL is only a fallback. */
  coverKey?: string | null;
  /** Where this reopens — sanitised at download time, never the address it was started from. */
  entryUrl?: string | null;
  entryCount: number;
  assetCount: number;
  presentCount: number;
  missingCount: number;
  /** Of the audio keys this collection needs, how many the bucket no longer has. */
  missingAudioCount?: number;
  audioCount?: number;
  /**
   * Every audio file is gone. A browser may reclaim script-writable storage under disk pressure and
   * `persist()` is a request, not a guarantee — so this is a real state, and one that has to be
   * said plainly rather than discovered by pressing play and hearing silence.
   */
  evicted?: boolean;
  /** Some of it still plays. */
  playable?: boolean;
  /** A newer release was seen online. Never acted on silently — see the store's version rules. */
  updateAvailable?: boolean;
  /** Tracks the collection has gained or lost since this was downloaded. Offered, never applied. */
  addedTrackCount?: number;
  removedTrackCount?: number;
  /** What it was downloaded at. */
  quality?: OfflineQuality;
  /** What removing this returns to the allowance — not its gross size. */
  removalSavings?: number;
  bytes: number;
  state: 'downloading' | 'ready' | 'partial';
  running: boolean;
  skippedTrackIds: string[];
  progress: { completed?: number; total?: number; bytes?: number; phase?: string } | null;
}

export interface OfflineStorageStatus {
  usedBytes: number;
  maxBytes: number;
  originUsage: number | null;
  quota: number | null;
  persisted: boolean;
}

/** The roster shape a download needs — the drawer's own cards already are one. */
export interface OfflineRosterEntry {
  voiceId: string;
  itemId?: string | null;
  entityType?: string | null;
  trackId?: string | null;
  trackVersion?: string | number | null;
  orbiterId?: string | null;
  orbiterVersion?: string | number | null;
  entangledWorldId?: string | null;
  entangledWorldVersion?: string | number | null;
  title?: string | null;
  subtitle?: string | null;
  image?: string | null;
  isOfficial?: boolean;
}

/** What a download is doing right now. Lives apart from the stored status on purpose — see below. */
export interface OfflineDownloadProgress {
  state?: string;
  phase?: 'resolving' | 'measuring' | 'interface' | 'downloading' | 'done' | 'cancelled';
  completed?: number;
  total?: number;
  bytes?: number;
  resolved?: number;
  resolvedTotal?: number;
}

export interface UseOfflineCollectionResult {
  /** Whether to offer the surface at all (the phase-1 flag). */
  enabled: boolean;
  /**
   * The first read of the store has landed. Before it, `status` is null for BOTH "never downloaded"
   * and "not looked yet" — anything deciding on that null (the arrival intent, for one) has to wait.
   */
  loaded: boolean;
  /** This collection's state, or null when it has never been downloaded. */
  status: OfflineCollectionStatus | null;
  /**
   * The live run, read straight from the downloader rather than off the stored manifest.
   *
   * They are separate because the first thing a download does is RESOLVE — a per-item hydration
   * that takes seconds each and produces no manifest until it finishes. A UI reading progress off
   * the stored status therefore showed nothing at all for the longest, least reassuring part of the
   * job, which is the one moment a person most needs to be told something is happening.
   */
  progress: OfflineDownloadProgress | null;
  /** Every downloaded collection, for the list. */
  library: OfflineCollectionStatus[];
  storage: OfflineStorageStatus | null;
  maxCollections: number;
  /** A limit refused something. Cleared by the next successful action. */
  capError: { reason: string; detail: Record<string, unknown> } | null;
  /** A download failed for a reason that is not a limit. */
  error: string | null;
  busy: boolean;
  /** Omit the quality to keep whatever this collection already is. */
  download: (quality?: OfflineQuality) => Promise<void>;
  resume: () => Promise<void>;
  cancel: () => Promise<void>;
  remove: (collectionId?: string) => Promise<void>;
}

/**
 * @param collectionId the collection on screen, or null outside collection mode
 * @param entries the drawer's cards — the roster a download resolves from
 * @param collectionName display only, stored so the downloads list can name it with no network
 * @param collectionLayout the saved arrangement, stored so an offline boot is arranged the same way
 */
export function useOfflineCollection({
  collectionId,
  entries,
  collectionName = null,
  collectionLayout = null,
}: {
  collectionId: string | null;
  entries: OfflineRosterEntry[];
  collectionName?: string | null;
  /** The saved arrangement, stored with the download so an offline boot restores it too. */
  collectionLayout?: object | null;
}): UseOfflineCollectionResult {
  const enabled = useMemo(() => isOfflineDownloadsEnabled(), []);
  const [status, setStatus] = useState<OfflineCollectionStatus | null>(null);
  const [progress, setProgress] = useState<OfflineDownloadProgress | null>(null);
  const [library, setLibrary] = useState<OfflineCollectionStatus[]>([]);
  const [storage, setStorage] = useState<OfflineStorageStatus | null>(null);
  const [maxCollections, setMaxCollections] = useState(0);
  const [capError, setCapError] = useState<UseOfflineCollectionResult['capError']>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Reads are async and a component can unmount mid-read; without this a resolved read would set
  // state on a gone component and, worse, could land AFTER a newer read and show the older answer.
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    const ticket = (generation.current += 1);
    try {
      const [collection, all] = await Promise.all([
        collectionId ? readCollectionOfflineStatus(collectionId) : Promise.resolve(null),
        readOfflineLibrary(),
      ]);
      if (ticket !== generation.current) return;
      setStatus(collection as OfflineCollectionStatus | null);
      setProgress(collectionId ? (readDownloadProgress(collectionId) as OfflineDownloadProgress | null) : null);
      setLibrary(all.collections as OfflineCollectionStatus[]);
      setStorage(all.storage as OfflineStorageStatus);
      setMaxCollections(all.maxCollections);
      setLoaded(true);
    } catch (readError) {
      console.warn('[offline] Could not read the offline library.', readError);
      // Still "looked". A failed read leaves `status` null, which reads the same as "never
      // downloaded" — and that is the honest answer to act on. Latching `loaded` false here instead
      // would strand anything waiting on the first read (the `?offline=download` intent) forever,
      // with nothing on screen to say why.
      if (ticket === generation.current) setLoaded(true);
    }
  }, [collectionId, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    // A new collection has not been looked at yet. Without this, the window before its read lands
    // reports `loaded` alongside the PREVIOUS collection's status — long enough for a waiting
    // intent to decide "already stored, leave it alone" about a collection never downloaded.
    setLoaded(false);
    void refreshOfflineSnapshot().then(() => refresh());
    return subscribeToOfflineLibrary((kind: string) => {
      // A progress tick is answered from memory. Re-reading the store on each one meant checking
      // every file in the manifest for every file stored — a sweep that grew with the square of the
      // collection and, on a real one, froze the page.
      if (kind === OFFLINE_CHANGE.PROGRESS) {
        setProgress(
          collectionId ? (readDownloadProgress(collectionId) as OfflineDownloadProgress | null) : null,
        );
        return;
      }
      void refresh();
    });
  }, [collectionId, enabled, refresh]);

  const run = useCallback(
    async (work: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      setCapError(null);
      try {
        await work();
      } catch (actionError) {
        if ((actionError as Error)?.name === 'AbortError') return;
        if (actionError instanceof OfflineCapError) {
          setCapError({ reason: actionError.reason, detail: actionError.detail });
        } else {
          setError((actionError as Error)?.message || String(actionError));
        }
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [refresh],
  );

  const download = useCallback(
    async (quality?: OfflineQuality) => {
      if (!collectionId) return;
      await run(() =>
        downloadCollection(
          {
            collectionId,
            name: collectionName,
            layout: collectionLayout,
            // Every item that names an entity, not only the ones with a track behind them: an orbiter
            // on a stage is an instrument with no audio file, and a world is a place, and both belong
            // in a download. The manifest filters again; this only keeps the resolve loop off cards
            // that name nothing at all.
            roster: entries.filter(
              (entry) => entry.trackId || entry.orbiterId || entry.entangledWorldId,
            ),
          },
          // Undefined is not "compressed": it means "whatever this collection already is", which is
          // what a plain re-download of a lossless collection must stay.
          { quality: quality ?? null },
        ),
      );
    },
    [collectionId, collectionName, collectionLayout, entries, run],
  );

  const resume = useCallback(async () => {
    if (!collectionId) return;
    await run(() => resumeCollectionDownload(collectionId));
  }, [collectionId, run]);

  const cancel = useCallback(async () => {
    if (!collectionId) return;
    await run(() => cancelCollectionDownload(collectionId));
  }, [collectionId, run]);

  const remove = useCallback(
    async (target?: string) => {
      const id = target || collectionId;
      if (!id) return;
      await run(() => removeDownloadedCollection(id));
    },
    [collectionId, run],
  );

  return {
    enabled,
    loaded,
    status,
    progress,
    library,
    storage,
    maxCollections,
    capError,
    error,
    busy,
    download,
    resume,
    cancel,
    remove,
  };
}
